import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { researchCardPostdocUpload } from "../../../core/service/researchCardPostdoc.service";
import { getNationality } from "../../../core/service/users.service";
import Swal from "sweetalert2";

// material
import {
  Box,
  Container,
  FormControl,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Typography,
  TextField,
  Stack
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
// components
import Page from "../../../components/Page";

// ----------------------------------------------------------------------
// const ROLE_ADMIN = "admin";
// const ROLE_MENTOR = "mentor";
// const ROLE_STD = "std";

export default function FormForMaking() {
  const navigate = useNavigate();
  const { user: currentUser } = useSelector((state) => state.authReducer);

  const FormMakingSchema = Yup.object().shape({
    attach_your_picture: Yup.object()
      .shape({
        file: Yup.mixed()
      })
      .nullable()
      .required(`File is required`),
    attach_your_id_passport: Yup.object()
      .shape({
        file: Yup.mixed()
      })
      .nullable()
      .required(`File is required`),
    researcher_card: Yup.string().required("Is required"),
    additional_note: Yup.string()
  });

  const formik = useFormik({
    initialValues: {
      attach_your_picture: {
        file: {}
      },
      attach_your_id_passport: {
        file: {}
      },
      researcher_card: "",
      additional_note: ""
    },
    validationSchema: FormMakingSchema,
    onSubmit: async (values) => {
      var formData = new FormData();
      for (const [key, value] of Object.entries(values)) {
        if (value?.file) {
          formData.append(key, value.file);
        } else {
          formData.append(key, value);
        }
      }
      formData.append("user_id", currentUser.id);

      await researchCardPostdocUpload(formData).then((e) => {
        return Swal.fire({
          icon: "success",
          title: "Successfully",
          showConfirmButton: false,
          timer: 1500
        }).then(async (result) => {
          const profile = await getNationality(currentUser?.id);
          if (profile?.nationality !== "Thai")
            return navigate("/form-visa-extension/post_doc", {
              replace: true
            });
          else
            return navigate("/renewal-std/post_doc", {
              replace: true
            });
        });
      });
      await new Promise((r) => setTimeout(r, 500));
    }
  });

  const {
    errors,
    getFieldProps,
    handleChange,
    handleSubmit,
    isSubmitting,
    touched,
    values,
    setFieldValue
  } = formik;

  return (
    <Page title="Form for Making a Researcher Card and Internet Account">
      <Container maxWidth="xl">
        <Box sx={{ pb: 5 }}>
          <Typography variant="h4">Form for Making a Research Card</Typography>
        </Box>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack
              direction="column"
              justifyContent="space-evenly"
              alignItems="stretch"
              spacing={2}
            >
              <Typography variant="subtitle1">
                C2F and Office of Human Resource has improved the working
                process to make process more convenient for postdoc to issue
                Chula ID card as well as Chula's email via online platform. We
                would like to ask your cooperation to carefully fill in the form
                as follows;
              </Typography>

              <List>
                <ListItem>
                  <ListItemText primary="Preparing a photo for Chula ID card:" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="- Picture size: width 1-2 inches, height 1-2 inches" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="- Digital photo size: width 600 pixels (maximum), height 600 pixels (maximum)" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="- Resolution: 600 DPI" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="- Background color blue or white" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="- Save the picture by your name and sumame (In Thai)" />
                </ListItem>
              </List>

              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  Please attach your picture*
                </Typography>
                <TextField
                  inputProps={{
                    accept:
                      "image/x-png,image/gif,image/jpeg,.doc,.docx,application/pdf"
                  }}
                  type="file"
                  onChange={(event) => {
                    setFieldValue(
                      `attach_your_picture.file`,
                      event.currentTarget.files[0]
                    );
                  }}
                  error={Boolean(
                    touched.attach_your_picture && errors.attach_your_picture
                  )}
                  helperText={
                    touched.attach_your_picture && errors.attach_your_picture
                  }
                />
              </FormControl>

              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  Please attach a copy of your ID or Passport no.*
                </Typography>
                <TextField
                  inputProps={{
                    accept:
                      "image/x-png,image/gif,image/jpeg,.doc,.docx,application/pdf"
                  }}
                  type="file"
                  onChange={(event) => {
                    setFieldValue(
                      `attach_your_id_passport.file`,
                      event.currentTarget.files[0]
                    );
                  }}
                  error={Boolean(
                    touched.attach_your_id_passport &&
                      errors.attach_your_id_passport
                  )}
                  helperText={
                    touched.attach_your_id_passport &&
                    errors.attach_your_id_passport
                  }
                />
              </FormControl>

              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  This researcher card for:
                </Typography>
                <TextField
                  select
                  value={values.researcher_card}
                  onChange={(e) => handleChange(e)}
                  {...getFieldProps("researcher_card")}
                  error={Boolean(
                    touched.researcher_card && errors.researcher_card
                  )}
                  helperText={touched.researcher_card && errors.researcher_card}
                >
                  <MenuItem value={"first_year_contract"}>
                    The first year contract
                  </MenuItem>
                  <MenuItem value={"second_year_contract"}>
                    The second year contract
                  </MenuItem>
                  <MenuItem value={"third_year_contract"}>
                    The third year contract
                  </MenuItem>
                  <MenuItem value={"extension_period"}>
                    Extension period
                  </MenuItem>
                </TextField>
              </FormControl>

              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  Additional Note for your case (if you have)
                </Typography>
                <TextField
                  multiline
                  rows={5}
                  {...getFieldProps("additional_note")}
                  error={Boolean(
                    touched.additional_note && errors.additional_note
                  )}
                  helperText={touched.additional_note && errors.additional_note}
                />
              </FormControl>

              <Typography>
                Please explain us about your case more, if you have to change
                type of visa, cancelling the previous visa that you have, before
                starting the new visa under C2F.
              </Typography>

              <Typography>
                Please recheck your information before submitting the form.
              </Typography>

              <Stack
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
                spacing={12}
                sx={{ paddingTop: 3 }}
              >
                <LoadingButton
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                >
                  Submit
                </LoadingButton>
              </Stack>
            </Stack>
          </Form>
        </FormikProvider>
      </Container>
    </Page>
  );
}
